import React, { PropsWithChildren, useRef } from 'react';
import { useDrop } from 'react-dnd';
import { ICollection } from 'types';
import { theme } from 'styles';
import { ITEM_TYPE } from './constants';

interface Props {
  onDrop: (item: { collection: ICollection }, index: number) => void;
}

function CollectionList({ onDrop, children }: PropsWithChildren<Props>) {
  const listRef = useRef<HTMLDivElement>(null);

  const [{ isOver }, drop] = useDrop(() => ({
    accept: ITEM_TYPE,
    drop: (item: { collection: ICollection }, monitor) => {
      const dropPosition = monitor.getClientOffset();
      if (!dropPosition || !listRef.current) return;

      const rows = Array.from(
        listRef.current.querySelectorAll<HTMLElement>('[data-collection-id]'),
      ).filter((el) => Number(el.dataset.collectionId) !== item.collection.id);
      const index = rows.filter((el) => {
        const rect = el.getBoundingClientRect();
        return rect.top + rect.height / 2 < dropPosition.y;
      }).length;

      onDrop(item, index);
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  return (
    <div
      ref={(node) => {
        drop(node);
        if (node) {
          // Using MutableRefObject to avoid read-only error
          (listRef as React.MutableRefObject<HTMLDivElement | null>).current =
            node;
        }
      }}
      css={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 8,
        margin: -8,
        boxSizing: 'content-box',
        borderRadius: theme.radius.lg,
        background: isOver ? theme.color.grey50 : 'transparent',
        transition: 'background-color 0.15s ease',
      }}
    >
      {children}
    </div>
  );
}

export default CollectionList;
